<template>
    <Card :title="$t('labels.delivery')">
        <div class="card-body">
            <div class="row">
                <FormGroup class="col-lg-3 col-md-6" :class="{'mb-0': isCustom}" :label="$t('labels.method')">
                    <DeliveryMethodSelect v-model="method" disabled />
                </FormGroup>
                <FormGroup class="col-lg-9 col-md-6" :class="{'mb-0': isCustom}" :label="$t('labels.address')">
                    <Input v-model="address"/>
                </FormGroup>
            </div>
            <template v-if="!isCustom">
                <div class="row">
                    <FormGroup class="col-lg-2 col-md-6" :label="$t('labels.city')">
                        <AppAutoComplete
                            :url="`/projects/${$route.params.projectUuid}/crm/delivery/providers/${method}/cities`"
                            :model-value="city?.uuid"
                            :selected-value="formatDeliveryCityName(city)"
                            @update:model-value="(newCity) => {city = newCity; warehouse = null}"
                            :queryParams="{
                                orders: [
                                    { column: 'name', dir: 'asc'}
                                ]
                            }"
                            can-be-empty
                        >
                            <template #optionItem="{option}">
                                {{ formatDeliveryCityName(option) }}
                            </template>
                        </AppAutoComplete>
                    </FormGroup>
                    <FormGroup class="col-lg-5 col-md-6" :label="$t('labels.department')">
                        <AppAutoComplete
                            :url="`/projects/${$route.params.projectUuid}/crm/delivery/providers/${method}/cities/${city?.uuid}/warehouses`"
                            :model-value="warehouse?.uuid"
                            :selected-value="warehouse?.name"
                            :disabled="!city"
                            @update:model-value="warehouse = $event"
                            :queryParams="{
                                orders: [
                                    { column: 'name', dir: 'asc'}
                                ]
                            }"
                            can-be-empty
                        />
                    </FormGroup>
                    <FormGroup class="col-lg-3 col-md-6" :label="$t('labels.recipient_name')">
                        <Input v-model="recipientName"/>
                    </FormGroup>
                    <FormGroup class="col-lg-2 col-md-6" :label="$t('labels.recipient_phone')">
                        <Input v-model="recipientPhone"/>
                    </FormGroup>
                </div>
                <div class="row">
                    <FormGroup class="col-lg-3 col-md-6 mb-0" :label="$t('labels.company_number')">
                        <InputGroup>
                            <Input v-model="companyNumber" />
                            <template #append>
                                <Button color="light" icon @click="showCreateWaybillModal()">
                                    <i class="icon-plus3"></i>
                                </Button>
                            </template>
                        </InputGroup>
                    </FormGroup>
                    <FormGroup class="col-lg-3 col-md-6 mb-0" :label="$t('labels.status')">
                        <template #labelAppend v-if="isUpdatingStatus">
                            <i class="icon-spinner2 spinner text-primary ml-2"></i>
                        </template>
                        <InputGroup>
                            <Input v-model="statusLabel" readonly />
                            <template #append>
                                <Button color="light" icon :disabled="isRefreshingStatus || !companyNumber" @click="refreshDelivery">
                                    <i class="icon-sync"></i>
                                </Button>
                            </template>
                        </InputGroup>
                    </FormGroup>
                    <FormGroup class="col-lg-3 col-md-6 mb-0" :label="$t('labels.delivery_zebra')">
                        <template #labelAppend v-if="isFetchingFiles">
                            <i class="icon-spinner2 spinner text-primary ml-2"></i>
                        </template>
                        <UniformFileUpload v-model="stickerFile" type="delivery-sticker" :existing-file="stickerFile" :disabled="!companyNumber" />
                    </FormGroup>
                    <FormGroup class="col-lg-3 col-md-6 mb-0" :label="$t('labels.delivery_invoice')">
                        <template #labelAppend v-if="isFetchingFiles">
                            <i class="icon-spinner2 spinner text-primary ml-2"></i>
                        </template>
                        <UniformFileUpload v-model="waybillFile" type="delivery-waybill" :existing-file="waybillFile" :disabled="!companyNumber" />
                    </FormGroup>
                </div>
            </template>
        </div>
        <div class="card-footer d-flex justify-content-end">
            <Button color="light" class="mr-2" @click="deleteDelivery">
                {{ $t('buttons.delete') }}
            </Button>
            <Button color="indigo" @click="saveDelivery">
                {{ $t('buttons.save') }}
            </Button>
        </div>
    </Card>
    <CreateRozetkaWaybillModal
        v-if="isRozetkaWaybill"
        ref="CreateRozetkaWaybillModal"
        :recipient-name="recipientName || order?.client?.name || ''"
        :recipient-phone="recipientPhone || order?.client?.phone || ''"
        :order-sum="order?.sum ?? 0"
        :order-uuid="$route.params.orderUuid"
        :integration-uuid="order.integration_order.integration.uuid"
        :delivery-uuid="$route.params.deliveryUuid"
        @created="applyDelivery($event)"
    />
    <CreateWaybillModal
        v-else
        ref="CreateWaybillModal"
        :method="method"
        :address="address"
        :recipient-name="recipientName || order?.client?.name || ''"
        :recipient-phone="recipientPhone || order?.client?.phone || ''"
        :order-sum="order?.sum ?? 0"
        :external-order-number="order?.external_order_number ?? ''"
        :order-uuid="$route.params.orderUuid"
        :delivery-uuid="$route.params.deliveryUuid"
        @created="applyDelivery($event)"
    />
</template>
<script>
import Card from "../../../../../components/new/CardComponent.vue";
import FormGroup from "../../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../../components/new/form/Input.vue";
import InputGroup from "../../../../../components/new/form/InputGroup.vue";
import Button from "../../../../../components/new/button/AppButton.vue";
import UniformFileUpload from "../../../../../components/new/file/UniformFileUpload.vue";
import AppAutoComplete from "../../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import DeliveryMethodSelect from "../../../../components/select/DeliveryMethodSelect.vue";
import CreateWaybillModal from "./modals/CreateWaybillModal.vue";
import CreateRozetkaWaybillModal from "./modals/CreateRozetkaWaybillModal.vue";

import { privateChannel } from "../../../../services/echoService";
import { mapGetters } from "vuex";

import { CUSTOM_DELIVERY_METHOD, DeliveryIntegrationsEnum } from "../../../../enums/DeliveryIntegrationsEnum";
import { IntegrationServiceEnum } from "../../../../enums/IntegrationServiceEnum";
import { formatDeliveryCityName } from "../../../../../utils";
import { httpClient } from "../../../../../services/HttpClient";
import MessageNotificator from "../../../../../services/MessageNotificator";

const FILES_FETCHED_EVENT = '\\App\\Events\\Crm\\Delivery\\DeliveryFilesFetchedEvent';
const STATUS_UPDATED_EVENT = '\\App\\Events\\Crm\\Delivery\\DeliveryStatusUpdatedEvent';

export default {
    name: 'DeliveryEditPage',
    components: {
        Card, FormGroup, Input, InputGroup, Button, UniformFileUpload, AppAutoComplete, DeliveryMethodSelect, CreateWaybillModal,
        CreateRozetkaWaybillModal
    },
    props: {
        order: {
            type: Object,
            default: null
        }
    },
    emits: ['update'],
    data() {
        return {
            method: CUSTOM_DELIVERY_METHOD,
            address: '',
            recipientName: '',
            recipientPhone: '',
            companyNumber: '',
            statusLabel: '',
            stickerFile: null,
            waybillFile: null,
            city: null,
            warehouse: null,
            isRefreshing: false,
            isFetchingFiles: false,
            isUpdatingStatus: false
        }
    },
    computed: {
        ...mapGetters({
            project: "route/project"
        }),
        isCustom() {
            const isCustom = this.method === CUSTOM_DELIVERY_METHOD;

            return isCustom;
        },
        isRozetkaWaybill() {
            const isRozetkaWaybill = this.method === DeliveryIntegrationsEnum.ROZETKA
                && this.order?.integration_order?.integration?.service === IntegrationServiceEnum.ROZETKA;

            return isRozetkaWaybill;
        },
        /**
         * The request itself is short-lived: the api only queues the job, so the button has to stay
         * locked until the status update actually finishes.
         */
        isRefreshingStatus() {
            const isRefreshingStatus = this.isRefreshing || this.isUpdatingStatus;

            return isRefreshingStatus;
        }
    },
    methods: {
        formatDeliveryCityName,
        showCreateWaybillModal() {
            if (this.isRozetkaWaybill) {
                this.$refs.CreateRozetkaWaybillModal.show();

                return;
            }

            this.$refs.CreateWaybillModal.show();
        },
        /**
         * The two flags are the source of truth for the loaders: only the api knows which of the
         * jobs the tracking number actually put on the queue.
         */
        applyDelivery(delivery) {
            this.method = delivery.method || CUSTOM_DELIVERY_METHOD;
            this.address = delivery.address;
            this.recipientName = delivery.recipient_name;
            this.recipientPhone = delivery.recipient_phone;
            this.companyNumber = delivery.company_number;
            this.statusLabel = delivery.status_label || '';
            this.stickerFile = delivery.sticker_file || null;
            this.waybillFile = delivery.waybill_file || null;
            this.city = delivery.city || null;
            this.warehouse = delivery.warehouse || null;
            this.isFetchingFiles = delivery.is_fetching_files;
            this.isUpdatingStatus = delivery.is_updating_status;
        },
        async fetchDelivery() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.$route.params.orderUuid}/deliveries/${this.$route.params.deliveryUuid}`);

            this.applyDelivery(response.data.data);
        },
        async refreshDelivery() {
            this.isRefreshing = true;

            try {
                const response = await httpClient.post(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.$route.params.orderUuid}/deliveries/${this.$route.params.deliveryUuid}/status`);

                this.applyDelivery(response.data.data);
            } finally {
                this.isRefreshing = false;
            }
        },
        async saveDelivery() {
            const request = {
                address: this.address || null,
                recipient_name: this.recipientName || null,
                recipient_phone: this.recipientPhone || null
            };

            if (!this.isCustom) {
                request.company_number = this.companyNumber?.replace(/\s/g, '') || null;
                request.city_uuid = this.city?.uuid ?? null;
                request.warehouse_uuid = this.warehouse?.uuid ?? null;

                if (this.waybillFile) {
                    request.waybill_file_uuid = this.waybillFile.uuid;
                }

                if (this.stickerFile) {
                    request.sticker_file_uuid = this.stickerFile.uuid;
                }
            }

            const response = await httpClient.put(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.$route.params.orderUuid}/deliveries/${this.$route.params.deliveryUuid}`, request);

            this.applyDelivery(response.data.data);
            this.$emit('update');

            new MessageNotificator().success(this.$t('alerts.success'))
        },
        deleteDelivery() {
            new MessageNotificator().confirm(this.$t('alerts.confirm_delete_delivery'), () => {
                httpClient.delete(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.$route.params.orderUuid}/deliveries/${this.$route.params.deliveryUuid}`).then(() => {
                    new MessageNotificator().success(this.$t('alerts.success'))
                    this.$emit('update')
                    this.$router.push({ name: 'OrderDeliveriesPage' })
                })
            })
        },
        subscribeToDeliveryEvents() {
            this.channel = privateChannel(`projects.${this.project.id}`)
                .listen(FILES_FETCHED_EVENT, this.onFilesFetched)
                .listen(STATUS_UPDATED_EVENT, this.onStatusUpdated);
        },
        unsubscribeFromDeliveryEvents() {
            if (!this.channel) {
                return;
            }

            this.channel.stopListening(FILES_FETCHED_EVENT, this.onFilesFetched);
            this.channel.stopListening(STATUS_UPDATED_EVENT, this.onStatusUpdated);

            this.channel = null;
        },
        onFilesFetched(event) {
            if (event.delivery_uuid !== this.$route.params.deliveryUuid) {
                return;
            }

            this.waybillFile = event.waybill_file || null;
            this.stickerFile = event.sticker_file || null;
            this.isFetchingFiles = false;
            this.$emit('update');
        },
        onStatusUpdated(event) {
            if (event.delivery_uuid !== this.$route.params.deliveryUuid) {
                return;
            }

            this.statusLabel = event.status?.name || '';
            this.isUpdatingStatus = false;
        }
    },
    mounted() {
        this.fetchDelivery();
        this.subscribeToDeliveryEvents();
    },
    unmounted() {
        this.unsubscribeFromDeliveryEvents();
    }
}
</script>
